import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';

// Ported from ptc-supervision `src/components/listFilters/QuickViewPills.tsx` (the fleet's preset-chip pattern, named in BL-124):
// a row of one-click presets with the active one filled. Deliberately plain <button aria-pressed>s, NOT a controlled Fluent
// Dropdown, so the aria-label/title render-loop hazard can't bite. Statetax's copy takes its group label from the caller (i18n via
// useTranslation) and supports a disabled pill (a "My" preset when the signed-in user resolves to no staff row).

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusCircular,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    cursor: 'pointer',
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
    ':disabled': { cursor: 'not-allowed', color: tokens.colorNeutralForegroundDisabled },
  },
  active: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandBackground}`,
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover, color: tokens.colorNeutralForegroundOnBrand },
  },
});

export interface QuickView {
  key: string;
  label: string;
  disabled?: boolean;
  /** Shown on hover; required to say WHY when disabled. */
  title?: string;
}

export interface QuickViewPillsProps {
  views: QuickView[];
  /** The pill to fill, or null when the current state matches none. */
  activeKey: string | null;
  onSelect: (key: string) => void;
  /** Accessible name of the group. */
  label: string;
}

export function QuickViewPills({ views, activeKey, onSelect, label }: QuickViewPillsProps) {
  const styles = useStyles();
  if (views.length === 0) return null;
  return (
    <div role="group" aria-label={label} className={styles.group}>
      {views.map((v) => {
        const active = v.key === activeKey;
        return (
          <button
            key={v.key}
            type="button"
            aria-pressed={active}
            disabled={v.disabled}
            title={v.title}
            onClick={() => onSelect(v.key)}
            className={mergeClasses(styles.pill, active && styles.active)}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}
